import React, { useState, useCallback, useMemo } from 'react'
import {
  Card,
  Form,
  Input,
  Typography,
  Flex,
  // ColorPicker,
  Upload,
  Button,
  Image,
  Select,
  Switch,
} from 'antd'
import {
  UploadOutlined,
  DeleteOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons'
// import type { Color } from 'antd/es/color-picker'
import type { FlowBranding } from '@/data'
import { getImages, type LibraryImage } from '@/api/lop/images/getImages'

const { Text } = Typography

type LogoMode = 'upload' | 'library'

interface BrandingEditorProps {
  branding: FlowBranding
  onChange: (branding: FlowBranding) => void
  enabled: boolean
  onToggle: (enabled: boolean) => void
}

function useLibraryImages() {
  const [images, setImages] = useState<LibraryImage[]>([])
  const [loading, setLoading] = useState(false)
  const [fetched, setFetched] = useState(false)

  const fetchImages = useCallback(async () => {
    setLoading(true)
    try {
      const result = await getImages()
      setImages(result)
    } catch {
      setImages([])
    } finally {
      setLoading(false)
      setFetched(true)
    }
  }, [])

  if (!fetched && !loading) {
    fetchImages()
  }

  const options = useMemo(
    () =>
      images.map((img) => ({
        value: img.url,
        label: img.name,
      })),
    [images],
  )

  return { images, options, loading }
}

export const BrandingEditor: React.FC<BrandingEditorProps> = ({
  branding,
  onChange,
  enabled,
  onToggle,
}) => {
  const [logoMode, setLogoMode] = useState<LogoMode>('upload')
  const {
    images: libraryImages,
    options: libraryOptions,
    loading: libraryLoading,
  } = useLibraryImages()

  const updateField = <K extends keyof FlowBranding>(
    key: K,
    value: FlowBranding[K],
  ) => {
    onChange({ ...branding, [key]: value })
  }

  // TODO: Re-enable with color palette
  // const handleColorChange = (key: keyof FlowBranding) => (color: Color) => {
  //   updateField(key, color.toHexString())
  // }

  const handleRemoveLogo = () => {
    onChange({ ...branding, logo: null, logo_url: null, logo_name: null })
  }

  const handleLibrarySelect = (url: string) => {
    const image = libraryImages.find((img) => img.url === url)
    onChange({
      ...branding,
      logo: null,
      logo_url: url,
      logo_name: image?.name ?? null,
    })
  }

  // New file upload takes priority, otherwise show existing URL from BE
  const logoPreview =
    branding.logo instanceof File
      ? URL.createObjectURL(branding.logo)
      : branding.logo_url || null

  return (
    <Card
      title={
        <Flex align="center" gap="middle">
          <Switch checked={enabled} onChange={onToggle} />
          <span>Cobranding</span>
        </Flex>
      }
      style={{ maxWidth: 700 }}
    >
      {!enabled ? (
        <Text type="secondary">
          Enable cobranding to customize the logo and color palette for this
          flow.
        </Text>
      ) : (
        <Form layout="vertical">
          <Form.Item label="Partner Logo">
            <Flex vertical gap="small">
              {logoPreview && (
                <Flex align="center" gap="middle">
                  <Image
                    src={logoPreview}
                    alt={branding.logo_alt || 'Partner logo'}
                    height={48}
                    preview={false}
                    style={{ objectFit: 'contain', maxWidth: 200 }}
                  />
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleRemoveLogo}
                    size="small"
                  >
                    Remove
                  </Button>
                </Flex>
              )}

              {logoMode === 'upload' ? (
                <Flex gap="small">
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      updateField('logo', file)
                      return false
                    }}
                  >
                    <Button icon={<UploadOutlined />}>
                      {logoPreview ? 'Replace Logo' : 'Upload Logo'}
                    </Button>
                  </Upload>
                  <Button
                    icon={<FolderOpenOutlined />}
                    onClick={() => setLogoMode('library')}
                  >
                    Select from Library
                  </Button>
                </Flex>
              ) : (
                <Flex gap="small" style={{ width: '100%' }}>
                  <Select
                    showSearch
                    allowClear
                    placeholder="Search library..."
                    style={{ flex: 1 }}
                    loading={libraryLoading}
                    value={
                      !branding.logo && branding.logo_url
                        ? branding.logo_url
                        : undefined
                    }
                    onChange={handleLibrarySelect}
                    filterOption={(input, option) =>
                      (option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={libraryOptions}
                  />
                  <Button
                    icon={<UploadOutlined />}
                    onClick={() => setLogoMode('upload')}
                  >
                    Upload Instead
                  </Button>
                </Flex>
              )}
            </Flex>
          </Form.Item>

          <Form.Item label="Logo Alt Text">
            <Input
              value={branding.logo_alt}
              onChange={(e) => updateField('logo_alt', e.target.value)}
              placeholder="e.g. Partner Company Name"
            />
          </Form.Item>

          {/* TODO: Re-enable color palette
          <Text strong style={{ display: 'block', marginBottom: 12 }}>
            Color Palette
          </Text>
          <Flex wrap gap="large">
            <Form.Item label="Primary" style={{ marginBottom: 8 }}>
              <ColorPicker
                value={branding.primary_color}
                onChange={handleColorChange('primary_color')}
                showText
              />
            </Form.Item>
            <Form.Item label="Secondary" style={{ marginBottom: 8 }}>
              <ColorPicker
                value={branding.secondary_color}
                onChange={handleColorChange('secondary_color')}
                showText
              />
            </Form.Item>
            <Form.Item label="Accent" style={{ marginBottom: 8 }}>
              <ColorPicker
                value={branding.accent_color}
                onChange={handleColorChange('accent_color')}
                showText
              />
            </Form.Item>
            <Form.Item label="CTA Button" style={{ marginBottom: 8 }}>
              <ColorPicker
                value={branding.cta_color}
                onChange={handleColorChange('cta_color')}
                showText
              />
            </Form.Item>
          </Flex>
          */}
        </Form>
      )}
    </Card>
  )
}
